import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShoppingBag, ArrowRight } from "lucide-react";

/**
 * Mini-carousel des Packs Métiers pour la page Login.
 * Auto-scroll horizontal infini (duplique la liste pour effet seamless).
 * Liens vers /packs-vitrine.
 */
export default function PacksCarousel() {
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/packs");
        if (!cancel) setPacks(data.packs || []);
      } catch {
        /* public endpoint — silencieux si erreur */
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (!packs.length) return null;
  const loop = [...packs, ...packs]; // duplique pour le scroll infini

  return (
    <div className="w-full" data-testid="login-packs-carousel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--pit-yellow)" }}>
            Bibliothèques pré-remplies
          </div>
          <div className="font-display text-lg leading-none" style={{ color: "var(--chalk)" }}>
            {packs.length} Packs Métiers · {packs.reduce((s, p) => s + (p.items_count || 0), 0)}+ ouvrages
          </div>
        </div>
        <Link
          to="/packs-vitrine"
          className="text-[12px] inline-flex items-center gap-1 hover:underline"
          style={{ color: "var(--pit-yellow)" }}
          data-testid="login-carousel-vitrine-link"
        >
          Voir tout <ArrowRight size={12} />
        </Link>
      </div>

      <div
        className="relative overflow-hidden rounded-lg border"
        style={{ borderColor: "var(--steel)", background: "var(--carbon)" }}
      >
        {/* Fade gauche/droite */}
        <div
          className="absolute inset-y-0 left-0 w-12 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to right, var(--carbon), transparent)" }}
        />
        <div
          className="absolute inset-y-0 right-0 w-12 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to left, var(--carbon), transparent)" }}
        />

        <div className="packs-scroll-track flex gap-3 py-4">
          {loop.map((p, i) => (
            <Link
              key={`${p.id}-${i}`}
              to="/packs-vitrine"
              className="shrink-0 w-[240px] p-3 rounded-md border transition-all hover:-translate-y-0.5"
              style={{ borderColor: "var(--steel)", background: "var(--carbon-2)" }}
              data-testid={`carousel-item-${p.id}-${i < packs.length ? "a" : "b"}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "var(--grey)" }}>
                  {p.trade}
                </div>
                <ShoppingBag size={14} style={{ color: "var(--pit-yellow)" }} />
              </div>
              <div className="font-display text-[15px] leading-tight mb-1" style={{ color: "var(--chalk)" }}>
                {p.name.replace(/^Pack\s+/i, "").replace(/\s+Pro$/i, "")}
              </div>
              <div className="text-[11px] mb-3 line-clamp-2" style={{ color: "var(--ink-soft)" }}>
                {p.tagline}
              </div>
              <div className="flex items-baseline justify-between border-t pt-2" style={{ borderColor: "var(--steel)" }}>
                <span className="font-display text-base" style={{ color: "var(--pit-yellow)" }}>{p.items_count}</span>
                <span className="text-[10px]" style={{ color: "var(--grey)" }}>ouvrages</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        .packs-scroll-track {
          animation: packs-scroll 45s linear infinite;
          width: max-content;
        }
        .packs-scroll-track:hover {
          animation-play-state: paused;
        }
        @keyframes packs-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .packs-scroll-track { animation: none; }
        }
      `}</style>
    </div>
  );
}
